import { DateRangePicker } from '@/components/DateRangePicker';
import { Can } from '@/components/can/Can';
import { Tabs } from '@/components/tabs/Tabs';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { ReferralDetailHeader } from '@/features/dashboard/referralReports/components/ReferralDetailHeader';
import { useReferralDetailPage } from '@/features/dashboard/referralReports/hooks/useReferralDetailPage';
import { IndividualCustomerReferralTable } from '@/features/dashboard/referralReports/table/individualCustomerReferral';
import { createReferralLinks } from '@/features/dashboard/referralReports/utils/createReferralLinks';
import { Translation } from '@/hoslog-intl';
import { UserIcon } from '@/icons';
import { affiliatesListQueries } from '@/services/affiliate/affiliate.queries';
import { referralReportQueries } from '@/services/dashboard/referralReports/referralReports.queries';
import { ReferralUserType } from '@/services/dashboard/referralReports/referralReports.types';
import { createFileRoute, SearchSchemaInput } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

const detailSearchSchema = z.object({
  q: z
    .any()
    .transform((val) => (val == null ? undefined : String(val)))
    .optional(),
  breadcrumbReturnTo: z.string().optional(),
  referCode: z.string().optional(),
  page: z.coerce.number().int().min(0).catch(0),
  fetch: z.coerce.number().int().min(1).catch(10),
  stageCode: z.enum(['stage_1', 'stage_2']).optional(),
  referralType: z.enum(['direct', 'indirect']).optional(),
  sort: z
    .preprocess(
      (value) => {
        if (typeof value !== 'string') return value;
        try {
          return JSON.parse(value);
        } catch {
          return undefined;
        }
      },
      z.array(z.object({ id: z.string(), desc: z.boolean() })),
    )
    .optional(),
});

declare module '@tanstack/react-router' {
  interface HistoryState {
    affiliateReferralName?: string;
    affiliateReferralIsFromSubDomain?: boolean;
    affiliateReferralSubDomainUrl?: string;
  }
}

export const Route = createFileRoute('/(app)/dashboard/referral-reports/$reportType/$referrerId/')({
  loaderDeps: ({ search }) => ({ breadcrumbReturnTo: search.breadcrumbReturnTo, referCode: search.referCode }),
  loader: async ({ params, context, location, deps }) => {
    let name = params.reportType === 'affiliate' ? location.state.affiliateReferralName : undefined;
    let referralCode = params.reportType === 'affiliate' ? deps.referCode : undefined;
    const isFromSubDomain = location.state.affiliateReferralIsFromSubDomain;
    const subDomainUrl = isFromSubDomain ? location.state.affiliateReferralSubDomainUrl : '';

    if (params.reportType === 'customer') {
      const queryOptions = referralReportQueries.customerList({
        referrerCustomerId: params.referrerId,
        row: 0,
        fetch: 1,
      });
      const response = await context.queryClient.ensureQueryData({
        ...queryOptions,
        queryFn: queryOptions.queryFn(context.client),
      });
      const customer = response.data?.[0];
      name = customer?.referrerCustomerNickname?.trim() || customer?.referrerCustomerFullname?.trim();
      referralCode = customer?.referrerCustomerReferCode;
    }

    if (params.reportType === 'affiliate' && !name) {
      const queryOptions = affiliatesListQueries.detail(params.referrerId);
      const { data: affiliate } = await context.queryClient.ensureQueryData({
        ...queryOptions,
        queryFn: queryOptions.queryFn(context.client),
      });
      name =
        [affiliate.lastName, affiliate.firstName].filter(Boolean).join(' ').trim() ||
        affiliate.companyName ||
        affiliate.userName;
    }

    return {
      name,
      referralCode,
      isFromSubDomain,
      subDomainUrl,
      crumb: name ?? 'Unknown user',
      breadcrumbItems: [
        {
          label: (
            <span className="flex items-center gap-1">
              <UserIcon size={16} />
              <Translation
                id={params.reportType === 'affiliate' ? 'common.form.label.affiliate' : 'common.form.label.customer'}
              />
            </span>
          ),
          path: '/dashboard/referral-reports',
          search: {
            userType: params.reportType === 'affiliate' ? ReferralUserType.Affiliate : ReferralUserType.Customer,
          },
        },
        {
          label: (
            <span className="flex items-center gap-1">
              <UserIcon size={16} />
              {name ?? 'Unknown user'}
            </span>
          ),
          path: location.pathname,
        },
      ],
      title: (
        <>
          <Translation id="dashboard.referralReports.pageTitle.referReport" />:{' '}
          <span className="text-destructive">{name ?? 'Unknown user'}</span>
        </>
      ),
    };
  },
  component: RouteComponent,
  validateSearch: (search: SearchSchemaInput) => detailSearchSchema.parse(search),
});

function RouteComponent() {
  const { reportType, referrerId } = Route.useParams();
  const { name, referralCode, isFromSubDomain, subDomainUrl } = Route.useLoaderData();
  const { dateRange, setDateRange, items, isCustomer } = useReferralDetailPage(reportType);
  const [activeTab, setActiveTab] = useState('customer');
  const referralLinks = createReferralLinks({
    activeTab,
    isFromSubDomain,
    referralCode,
    referrerId,
    subDomainUrl,
  });

  return (
    <div className="flex flex-col gap-[18px]">
      <div className="relative">
        <div className="absolute right-0 -top-[66px]">
          <DateRangePicker
            value={dateRange}
            onRangeSelect={setDateRange}
            defaultPreset="today"
            showDefaultRangeWhenEmpty={false}
            className="border-primary hover:border-primary bg-transparent hover:bg-transparent hover:opacity-100 text-primary hover:text-primary"
            iconClassName="text-primary group-hover:text-primary"
            hideChevron
          />
        </div>
      </div>
      {referralLinks && (
        <ReferralDetailHeader
          name={name ?? 'Unknown user'}
          referralUrl={referralLinks.referralUrl}
          qrUrl={referralLinks.qrUrl}
        />
      )}
      {isCustomer ? (
        <Can rule={{ allOf: [PERMS.referral.customerReport.read] }}>
          <IndividualCustomerReferralTable dateRange={dateRange} />
        </Can>
      ) : (
        <Can rule={{ allOf: [PERMS.affiliate.referral.report.view] }}>
          <Tabs
            className="rounded-[8px] bg-muted shadow"
            listClassName="bg-muted"
            triggerClassName="bg-muted rounded-t-[8px] px-6 py-3"
            defaultActive="customer"
            value={activeTab}
            onChange={setActiveTab}
            items={items}
          />
        </Can>
      )}
    </div>
  );
}
